import type { Dispatch, SetStateAction } from "react";

import { useEffect } from "react";

import FeedbackAudio from "@/features/request/feedback-tool/feedbackTool/audio";
import { FeedbackList } from "@/features/request/feedback-tool/feedbackTool/components/FeedbackList.tsx";
import { FeedbackModal } from "@/features/request/feedback-tool/feedbackTool/components/FeedbackModal.tsx";
import { ZoomControls } from "@/features/request/feedback-tool/feedbackTool/components/ZoomControls.tsx";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import FeedBackImage from "@/features/request/feedback-tool/feedbackTool/image";
import PFD from "@/features/request/feedback-tool/feedbackTool/pdf";
import FeedbackVideo from "@/features/request/feedback-tool/feedbackTool/video";

const VIDEO_FORMATS = [".avi", ".mov", ".mkv", ".mp4", ".m4v", ".gif"];
const IMAGE_FORMATS = [".png", ".bmp", ".webp", ".jpg", ".jpeg", ".svg"];
const PDF_FORMATS = [".pdf"];
const AUDIO_FORMATS = [".mp3", ".wav"];

type Attachment = {
  id: number;
  name: string;
  path: string;
  url: string;
};

interface FeedbackToolProps {
  attachment: Attachment;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export default function FeedbackTool({
  attachment,
  open,
  setOpen,
}: FeedbackToolProps) {
  const {
    divImgRef,
    feedback: feedBack,
    formFeedBack,
    imageOnly,
    img,
    isTablet,
    resetZoomAndControls,
    selected,
    setFormFeedBack,
    setImg,
    setSelected,
    setSize,
    size,
  } = useFeedbackTool();

  if (attachment && img.id !== attachment.id) {
    setImg({
      asset: attachment.name,
      id: attachment.id,
      url: attachment.url,
    });
  }

  const isPdf = img.url.includes(".pdf");

  useEffect(() => {
    if (open) resetZoomAndControls();
  }, [open]);

  return (
    <div className="relative p-10 bg-bg-primary">
      {open ? (
        <FeedbackModal setOpen={setOpen} setSize={setSize} size={size}>
          <div
            className={`relative flex flex-col sm:flex-row sm:justify-between ${isTablet && imageOnly ? "" : "mb-5 ml-5 pr-10 sm:pr-0"} ${size === "MODAL" ? "" : "h-full w-full"}`}
          >
            {isTablet ? (
              <div
                className={`relative bg-border-secondary h-1/2 w-full min-w-0 overflow-hidden ${size === "FULL" ? "sm:h-[calc(100%-53px)]" : "sm:h-auto"}`}
              >
                <div
                  className={`w-full min-w-0 ${isPdf ? "" : "flex justify-center items-center"}`}
                  ref={divImgRef}
                  style={{
                    height: "100%",
                    maxHeight: "inherit",
                    overflow: "auto",
                  }}
                >
                  <RenderFormat media={img.url} />
                </div>
                <ZoomControls />
              </div>
            ) : (
              <>
                <ZoomControls />
                <div
                  className={`relative bg-border-secondary flex h-1/2 sm:h-auto ${isPdf ? "" : "justify-center items-center"} ${size === "FULL" ? "w-full" : "min-h-full w-full"}`}
                  ref={divImgRef}
                  style={{
                    maxHeight: "calc(100% - 53px)",
                    overflow: size === "FULL" ? "auto" : "none",
                  }}
                >
                  <RenderFormat media={img.url} />
                </div>
              </>
            )}
            <div
              className={`w-full h-1/2 sm:h-full sm:w-122 pl-3 mt-3 ${size === "FULL" ? "mr-5 pb-15" : ""} ${isTablet && imageOnly ? "hidden" : ""}`}
            >
              <FeedbackList
                feedBacks={feedBack}
                formFeedback={formFeedBack}
                selected={selected}
                setFormFeedBack={setFormFeedBack}
                setSelected={setSelected}
                size={size}
              />
            </div>
          </div>
        </FeedbackModal>
      ) : null}
    </div>
  );
}

function RenderFormat({ media }: { media: string }) {
  const { img } = useFeedbackTool();

  if (VIDEO_FORMATS.some((format) => media.includes(format))) {
    return <FeedbackVideo media={img.url} />;
  }

  if (AUDIO_FORMATS.some((format) => media.includes(format))) {
    return <FeedbackAudio media={img.url} />;
  }

  if (IMAGE_FORMATS.some((format) => media.includes(format))) {
    return <FeedBackImage />;
  }

  if (PDF_FORMATS.some((format) => media.includes(format))) {
    return <PFD url={img.url} />;
  }
}
